import React, { useEffect, useState, useContext } from "react";
import axios from "../../api/axiosConfig";
import { ToastContainer, toast } from "react-toastify";
import { AuthContext } from "../../context/AuthContexto";
import { useNavigate } from "react-router-dom";
import "react-toastify/dist/ReactToastify.css";

function PedidoDetalle({ pedido, onClose }) {
  const { usuario } = useContext(AuthContext);
  const navigate = useNavigate();

  const [productos, setProductos] = useState([]);
  const [detalles, setDetalles] = useState([]);
  const [descripcion, setDescripcion] = useState(pedido.descripcion || "");
  const [total, setTotal] = useState(0);
  const [modoEdicion, setModoEdicion] = useState(false);
  const [mostrarConfirmacion, setMostrarConfirmacion] = useState(false);

  // 🧾 Ticket e impresión
  const [ticketPedido, setTicketPedido] = useState("");
  const [mostrarConfirmacionImpresion, setMostrarConfirmacionImpresion] =
    useState(false);

  // ✅ Carga inicial
  useEffect(() => {
    if (pedido?.detalles) {
      const detallesProcesados = pedido.detalles.map((d) => {
        const cantidad = parseFloat(d.cantidad) || 0;
        const subtotal = parseFloat(d.subtotal) || 0;
        const precio =
          d.precio && !isNaN(parseFloat(d.precio))
            ? parseFloat(d.precio)
            : cantidad > 0
            ? subtotal / cantidad
            : 0;

        const prodId = d.producto?.id || "";
        const prodNombre = d.producto?.nombre || "";

        return {
          ...d,
          producto: prodId,
          descripcion: d.descripcion || "",
          precio,
          subtotal: subtotal || cantidad * precio,
          // 🔍 campos para búsqueda en edición
          busqueda: prodNombre,
          sugerencias: [],
        };
      });

      setDetalles(detallesProcesados);
    }

    setDescripcion(pedido.descripcion || "");
    obtenerProductos();
  }, [pedido]);

  useEffect(() => {
    calcularTotal();
  }, [detalles]);

  const obtenerProductos = async () => {
    try {
      const res = await axios.get("/productos");
      setProductos(res.data || []);
    } catch (error) {
      console.error("Error al cargar productos:", error);
    }
  };

  const calcularTotal = () => {
    const totalCalc = detalles.reduce(
      (acc, d) => acc + (parseFloat(d.subtotal) || 0),
      0
    );
    setTotal(totalCalc);
  };

  const agregarDetalle = () => {
    setDetalles((prev) => [
      ...prev,
      {
        producto: "",
        cantidad: 1,
        precio: 0,
        subtotal: 0,
        descripcion: "",
        busqueda: "",
        sugerencias: [],
      },
    ]);
  };

  const actualizarDetalle = (index, campo, valor) => {
    const nuevosDetalles = [...detalles];
    nuevosDetalles[index][campo] = valor;

    // Si cambian producto por select (por si acaso)
    if (campo === "producto") {
      const prod = productos.find((p) => p.id == valor);
      nuevosDetalles[index].precio = prod ? prod.precio : 0;
      nuevosDetalles[index].busqueda = prod ? prod.nombre : "";
    }

    const cantidad = parseFloat(nuevosDetalles[index].cantidad) || 0;
    const precio = parseFloat(nuevosDetalles[index].precio) || 0;
    nuevosDetalles[index].subtotal = cantidad * precio;

    setDetalles(nuevosDetalles);
  };

  const eliminarDetalle = (index) => {
    const nuevos = detalles.filter((_, i) => i !== index);
    setDetalles(nuevos);
  };

  // 🔎 Buscar producto (misma lógica que PedidoCrear)
  const manejarBusquedaProducto = (index, valor) => {
    const nuevos = [...detalles];
    nuevos[index].busqueda = valor;

    if (valor.trim() === "") {
      nuevos[index].sugerencias = [];
    } else {
      const filtrados = productos
        .filter((p) => p.nombre.toLowerCase().includes(valor.toLowerCase()))
        .slice(0, 4);
      nuevos[index].sugerencias = filtrados;
    }

    setDetalles(nuevos);
  };

  // ✅ Seleccionar producto desde sugerencias
  const seleccionarProducto = (index, producto) => {
    if (producto.bloqueado) {
      toast.error("🚫 Este producto está bloqueado / sin stock");
      const nuevos = [...detalles];
      nuevos[index].sugerencias = [];
      setDetalles(nuevos);
      return;
    }

    const nuevos = [...detalles];
    nuevos[index].producto = producto.id;
    nuevos[index].precio = producto.precio;
    nuevos[index].busqueda = producto.nombre;
    nuevos[index].sugerencias = [];

    const cantidad = parseFloat(nuevos[index].cantidad) || 1;
    nuevos[index].subtotal = cantidad * producto.precio;

    setDetalles(nuevos);
  };

  const guardarCambios = async () => {
    try {
      const pedidoActualizado = {
        ...pedido,
        descripcion,
        detalles: detalles.map((d) => ({
          producto: { id: parseInt(d.producto) },
          cantidad: parseInt(d.cantidad),
          subtotal: parseFloat(d.subtotal),
          descripcion: d.descripcion || "",
        })),
      };

      await axios.put(`/pedidos/${pedido.id}`, pedidoActualizado);
      toast.success("✅ Pedido actualizado correctamente");
      setMostrarConfirmacion(true);
      setModoEdicion(false);
    } catch (error) {
      console.error("Error al guardar cambios:", error);
      toast.error("❌ Error al actualizar el pedido");
    }
  };

  const obtenerTicketPedido = async () => {
    try {
      const res = await axios.get(`/pedidos/${pedido.id}/ticket`);
      setTicketPedido(res.data);
      return res.data;
    } catch (error) {
      console.error(error);
      toast.error("❌ No se pudo generar el ticket del pedido");
      return null;
    }
  };

  const imprimirPedido = async () => {
    try {
      const ticket = ticketPedido || (await obtenerTicketPedido());
      if (!ticket) return;

      await axios.post("/imprimir", {
        impresora: "POS-80C",
        contenido: ticket,
      });

      toast.success("🖨️ Pedido enviado a impresión");
    } catch (error) {
      console.error(error);
      toast.error("❌ Error al imprimir");
    }
  };

  // ✅ Roles
  const esAdmin = usuario?.rol === "ADMIN";
  const esCajero = usuario?.rol === "CAJERO";
  const puedeEditar = esAdmin || (esCajero && pedido.entregado === false);
  const puedeFacturar = esAdmin || esCajero;

  // 🚀 Navegar a facturar
  const irAFacturar = () => {
    navigate(`/facturar/${pedido.id}`);
  };

  return (
    <div className="mt-4 px-3 md:px-6">
      <ToastContainer position="top-right" autoClose={3000} />

      {/* Card principal */}
      <div className="bg-white/95 dark:bg-slate-900/90 text-slate-900 dark:text-slate-100 rounded-2xl shadow-xl border border-slate-200 dark:border-slate-800 overflow-hidden">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 px-5 py-3 border-b border-slate-200 dark:border-slate-800 bg-slate-50/70 dark:bg-slate-900/70">
          <div className="flex items-center gap-2">
            <span className="text-amber-500 dark:text-amber-300">🧾</span>
            <div>
              <h2 className="text-base md:text-lg font-semibold text-slate-800 dark:text-slate-100">
                Detalle del Pedido #{pedido.id}
              </h2>
              <p className="text-xs text-slate-500 dark:text-slate-400">
                Revisa los productos, ajusta cantidades o genera la factura.
              </p>
            </div>
          </div>

          <div className="flex flex-wrap gap-2 justify-end">
            <button
              type="button"
              onClick={onClose}
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-slate-400 dark:border-slate-600 text-xs md:text-sm text-slate-700 dark:text-slate-100 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
            >
              ⬅️ Volver
            </button>

            {puedeEditar && (
              <button
                type="button"
                onClick={() => setModoEdicion((prev) => !prev)}
                className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs md:text-sm font-medium transition-colors ${
                  modoEdicion
                    ? "border border-rose-400 text-rose-500 bg-rose-50 dark:bg-rose-900/20 hover:bg-rose-100 dark:hover:bg-rose-900/30"
                    : "border border-sky-400 text-sky-600 bg-sky-50 dark:bg-sky-900/30 hover:bg-sky-100 dark:hover:bg-sky-900/50"
                }`}
              >
                {modoEdicion ? "❌ Cancelar edición" : "✏️ Editar pedido"}
              </button>
            )}

            {puedeFacturar && (
              <button
                type="button"
                onClick={irAFacturar}
                className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs md:text-sm font-medium border border-emerald-500 bg-emerald-500 text-white hover:bg-emerald-600 transition-colors"
              >
                💰 Facturar
              </button>
            )}

            <button
              type="button"
              onClick={() => setMostrarConfirmacionImpresion(true)}
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs md:text-sm font-medium border border-purple-500 bg-purple-500 text-white hover:bg-purple-600 transition-colors"
            >
              🖨️ Imprimir
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="px-5 py-4 space-y-4">
          {/* Info básica */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-xs sm:text-sm">
            <div>
              <span className="font-semibold text-slate-600 dark:text-slate-300">
                Mesa:{" "}
              </span>
              <span className="text-slate-800 dark:text-slate-100">
                {pedido.mesa?.numero || "—"}
              </span>
            </div>
            <div>
              <span className="font-semibold text-slate-600 dark:text-slate-300">
                Mesero:{" "}
              </span>
              <span className="text-slate-800 dark:text-slate-100">
                {pedido.mesero?.nombre || "—"}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <span className="font-semibold text-slate-600 dark:text-slate-300">
                Estado:
              </span>
              {pedido.entregado ? (
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300">
                  Entregado
                </span>
              ) : (
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300">
                  Pendiente
                </span>
              )}
            </div>
          </div>

          {/* Descripción */}
          <div>
            <span className="block text-xs font-medium text-slate-600 dark:text-slate-300 mb-1">
              Descripción
            </span>
            {modoEdicion ? (
              <textarea
                rows={2}
                className="w-full rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-950/70 text-xs sm:text-sm text-slate-800 dark:text-slate-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-transparent"
                value={descripcion}
                onChange={(e) => setDescripcion(e.target.value)}
                placeholder="Notas adicionales sobre el pedido..."
              />
            ) : (
              <div className="px-3 py-2 rounded-lg bg-slate-50/80 dark:bg-slate-950/60 border border-slate-200 dark:border-slate-700 text-xs sm:text-sm text-slate-700 dark:text-slate-100">
                {descripcion || "— Sin descripción —"}
              </div>
            )}
          </div>

          {/* Tabla de detalles */}
          <div className="mt-2">
            <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-100 mb-2">
              Productos
            </h3>
            <div className="overflow-x-auto rounded-xl border border-slate-200 dark:border-slate-700">
              <table className="min-w-full text-xs sm:text-sm">
                <thead className="bg-slate-900 text-slate-100">
                  <tr>
                    <th className="px-3 py-2 text-center font-semibold">
                      Producto
                    </th>
                    <th className="px-3 py-2 text-center font-semibold">
                      Cant.
                    </th>
                    <th className="px-3 py-2 text-center font-semibold">
                      Precio
                    </th>
                    <th className="px-3 py-2 text-center font-semibold">
                      Descripción
                    </th>
                    <th className="px-3 py-2 text-center font-semibold">
                      Subtotal
                    </th>
                    {modoEdicion && (
                      <th className="px-3 py-2 text-center font-semibold">
                        Acción
                      </th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {detalles.map((d, index) => (
                    <tr
                      key={index}
                      className="odd:bg-white even:bg-slate-50 dark:odd:bg-slate-900/70 dark:even:bg-slate-900/50 relative"
                    >
                      {/* Producto */}
                      <td className="px-3 py-2 text-center align-top">
                        {modoEdicion ? (
                          <div className="relative">
                            <input
                              type="text"
                              placeholder="Buscar producto..."
                              value={d.busqueda || ""}
                              onChange={(e) =>
                                manejarBusquedaProducto(index, e.target.value)
                              }
                              autoComplete="off"
                              className="w-full rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-950 px-2 py-1.5 text-[11px] sm:text-xs text-slate-900 dark:text-slate-100 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-transparent"
                            />
                            {d.sugerencias?.length > 0 && (
                              <ul className="absolute left-0 right-0 mt-1 max-h-40 overflow-y-auto rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-[11px] sm:text-xs text-slate-800 dark:text-slate-100 shadow-lg z-20">
                                {d.sugerencias.map((p) => (
                                  <li
                                    key={p.id}
                                    className={`px-3 py-1.5 flex justify-between gap-2 cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-800 ${
                                      p.bloqueado ? "opacity-60" : ""
                                    }`}
                                    onClick={() =>
                                      seleccionarProducto(index, p)
                                    }
                                  >
                                    <span>
                                      {p.nombre}
                                      {p.bloqueado && (
                                        <span className="ml-2 text-[10px] font-semibold text-rose-500">
                                          (bloqueado)
                                        </span>
                                      )}
                                    </span>
                                    <span className="text-slate-500 dark:text-slate-400">
                                      ${p.precio.toFixed(2)}
                                    </span>
                                  </li>
                                ))}
                              </ul>
                            )}
                          </div>
                        ) : (
                          <span className="text-slate-800 dark:text-slate-100">
                            {productos.find((p) => p.id == d.producto)?.nombre ||
                              "—"}
                          </span>
                        )}
                      </td>

                      {/* Cantidad */}
                      <td className="px-3 py-2 text-center">
                        {modoEdicion ? (
                          <input
                            type="number"
                            min="1"
                            className="w-20 mx-auto rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-950 text-[11px] sm:text-xs px-2 py-1 text-center focus:outline-none focus:ring-1 focus:ring-sky-500 text-slate-800 dark:text-slate-100"
                            value={d.cantidad}
                            onChange={(e) =>
                              actualizarDetalle(
                                index,
                                "cantidad",
                                e.target.value
                              )
                            }
                          />
                        ) : (
                          <span className="text-slate-800 dark:text-slate-100">
                            {d.cantidad}
                          </span>
                        )}
                      </td>

                      {/* Precio */}
                      <td className="px-3 py-2 text-center">
                        {modoEdicion ? (
                          <input
                            type="number"
                            step="0.01"
                            className="w-24 mx-auto rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-950 text-[11px] sm:text-xs px-2 py-1 text-center focus:outline-none focus:ring-1 focus:ring-sky-500 text-slate-800 dark:text-slate-100"
                            value={d.precio}
                            onChange={(e) =>
                              actualizarDetalle(index, "precio", e.target.value)
                            }
                          />
                        ) : (
                          <span className="text-slate-800 dark:text-slate-100">
                            ${parseFloat(d.precio || 0).toFixed(2)}
                          </span>
                        )}
                      </td>

                      {/* Descripción detalle */}
                      <td className="px-3 py-2 text-center">
                        {modoEdicion ? (
                          <input
                            type="text"
                            className="w-full rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-950 text-[11px] sm:text-xs px-2 py-1 focus:outline-none focus:ring-1 focus:ring-sky-500 text-slate-800 dark:text-slate-100"
                            value={d.descripcion}
                            onChange={(e) =>
                              actualizarDetalle(
                                index,
                                "descripcion",
                                e.target.value
                              )
                            }
                          />
                        ) : (
                          <span className="text-slate-700 dark:text-slate-100">
                            {d.descripcion || "—"}
                          </span>
                        )}
                      </td>

                      {/* Subtotal */}
                      <td className="px-3 py-2 text-center">
                        <span className="font-medium text-slate-800 dark:text-slate-100">
                          ${parseFloat(d.subtotal || 0).toFixed(2)}
                        </span>
                      </td>

                      {/* Acción */}
                      {modoEdicion && (
                        <td className="px-3 py-2 text-center">
                          <button
                            type="button"
                            onClick={() => eliminarDetalle(index)}
                            className="inline-flex items-center justify-center w-8 h-8 rounded-full border border-rose-400 text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-900/30 text-[11px] transition-colors"
                          >
                            ❌
                          </button>
                        </td>
                      )}
                    </tr>
                  ))}

                  {detalles.length === 0 && (
                    <tr>
                      <td
                        colSpan={modoEdicion ? 6 : 5}
                        className="px-3 py-4 text-center text-xs sm:text-sm text-slate-500 dark:text-slate-300"
                      >
                        No hay productos en este pedido.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Footer total + acciones edición */}
          <div className="mt-4 flex flex-col sm:flex-row items-center justify-between gap-3">
            {modoEdicion && (
              <button
                type="button"
                onClick={agregarDetalle}
                className="inline-flex items-center gap-2 px-3 py-1.5 text-xs md:text-sm rounded-lg border border-sky-500 text-sky-700 dark:text-sky-300 bg-sky-50 dark:bg-sky-900/30 hover:bg-sky-100 dark:hover:bg-sky-900/50 transition-colors"
              >
                ➕ Agregar producto
              </button>
            )}

            <div className="flex-1" />

            <h5 className="text-base md:text-lg font-semibold text-emerald-500 dark:text-emerald-400">
              Total: ${total.toFixed(2)}
            </h5>
          </div>

          {modoEdicion && (
            <div className="mt-4 flex justify-end">
              <button
                type="button"
                onClick={guardarCambios}
                className="inline-flex items-center gap-2 px-4 py-2 text-xs md:text-sm rounded-lg bg-emerald-500 hover:bg-emerald-600 text-white font-semibold border border-emerald-500 transition-colors"
              >
                💾 Guardar cambios
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Modal de confirmación actualización */}
      {mostrarConfirmacion && (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/60">
          <div className="relative max-w-md w-[95%] rounded-2xl border border-slate-200 dark:border-slate-700 bg-white/95 dark:bg-slate-900/95 shadow-2xl overflow-hidden">
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 dark:border-slate-700">
              <div className="flex items-center gap-2">
                <span className="flex h-6 w-6 items-center justify-center rounded-full bg-emerald-500 text-white text-xs">
                  ✓
                </span>
                <h3 className="text-sm md:text-base font-semibold text-slate-800 dark:text-slate-100">
                  Pedido actualizado
                </h3>
              </div>
              <button
                type="button"
                onClick={() => setMostrarConfirmacion(false)}
                className="text-xs px-2 py-1 rounded-lg border border-slate-300 dark:border-slate-600 text-slate-600 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
              >
                Cerrar
              </button>
            </div>

            {/* Body */}
            <div className="px-4 py-4 text-xs sm:text-sm space-y-2">
              <p className="text-slate-700 dark:text-slate-200">
                El pedido se ha guardado correctamente.
              </p>
              <p className="text-slate-600 dark:text-slate-300">
                ¿Deseas volver al listado de pedidos?
              </p>
            </div>

            {/* Footer */}
            <div className="px-4 py-3 border-t border-slate-200 dark:border-slate-700 flex justify-end gap-2 bg-slate-50/80 dark:bg-slate-900/80">
              <button
                type="button"
                onClick={() => setMostrarConfirmacion(false)}
                className="px-3 py-1.5 text-xs sm:text-sm rounded-lg border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-100 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
              >
                Permanecer aquí
              </button>
              <button
                type="button"
                onClick={() => {
                  setMostrarConfirmacion(false);
                  onClose();
                }}
                className="px-3 py-1.5 text-xs sm:text-sm rounded-lg bg-sky-500 hover:bg-sky-600 text-white font-semibold border border-sky-500 transition-colors"
              >
                ⬅️ Volver al listado
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal confirmación impresión */}
      {mostrarConfirmacionImpresion && (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/60">
          <div className="relative max-w-md w-[95%] rounded-2xl border border-slate-200 dark:border-slate-700 bg-white/95 dark:bg-slate-900/95 shadow-2xl overflow-hidden">
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 dark:border-slate-700">
              <h3 className="text-sm md:text-base font-semibold text-slate-800 dark:text-slate-100">
                🖨️ Confirmar impresión
              </h3>
              <button
                type="button"
                onClick={() => setMostrarConfirmacionImpresion(false)}
                className="text-xs px-2 py-1 rounded-lg border border-slate-300 dark:border-slate-600 text-slate-600 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
              >
                Cerrar
              </button>
            </div>

            {/* Body */}
            <div className="px-4 py-4 text-xs sm:text-sm space-y-2">
              <p className="text-slate-700 dark:text-slate-200">
                ¿Deseas imprimir el ticket de este pedido en la impresora{" "}
                <span className="font-semibold">POS-80C</span>?
              </p>
            </div>

            {/* Footer */}
            <div className="px-4 py-3 border-t border-slate-200 dark:border-slate-700 flex justify-end gap-2 bg-slate-50/80 dark:bg-slate-900/80">
              <button
                type="button"
                onClick={() => setMostrarConfirmacionImpresion(false)}
                className="px-3 py-1.5 text-xs sm:text-sm rounded-lg border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-100 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={async () => {
                  await imprimirPedido();
                  setMostrarConfirmacionImpresion(false);
                }}
                className="px-3 py-1.5 text-xs sm:text-sm rounded-lg bg-purple-600 hover:bg-purple-700 text-white font-semibold border border-purple-600 transition-colors"
              >
                🖨️ Imprimir
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default PedidoDetalle;
